import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { CDRTransactionInterface } from '../../interface/payment-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disablePaymentManagementApplied,
  fetchAllCDRTransactions,
  fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
  refreshAllCDRTransactions,
} from '../../redux/slices/PaymentManagementSlice';
import { setSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import CpoCdrSidePanel from '../../shared-components/table-side-panel/CpoCdrSidePanel';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { getDaysBetween } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

/**
 * Formats total time in hours into a human-readable duration string.
 * @param {number | string | undefined | null} totalTime - The total time value in hours.
 * @returns {string} The formatted duration string.
 */
const formatTotalTime = (totalTime: number | string | undefined | null): string => {
  if (totalTime == null || Number.isNaN(Number(totalTime))) {
    return '0';
  }

  const totalSeconds = Math.round(Number(totalTime) * 3600);
  if (totalSeconds <= 0) {
    return '0';
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const displayTime = [
    days ? `${days} d${days > 1 ? 's' : ''}` : '',
    hours ? `${hours} hr${hours > 1 ? 's' : ''}` : '',
    minutes ? `${minutes} min` : '',
    seconds ? `${seconds} s` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return displayTime || '0';
};

/**
 * Payments Management function Component
 * @returns {any} The rendered CPOTransactionManagement component
 */
function CPOTransactionManagement() {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.payments);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Fetch the payment details in redux
   * @param {any} data - The query payload for fetching transactions
   */
  const fetchPayments = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(fetchAllCDRTransactions(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...queryParameters, page, limit, ...sortValue });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: payments,
    });

  /**
   * Auto refresh data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAllCDRTransactions({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  const column = [
    {
      label: t('CDR ID'),
      accessorKey: 'cdrId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <CpoCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('Session ID'),
      accessorKey: 'sessionId' as keyof CDRTransactionInterface,
      queryParamsKey: 'transactionObjectId',
    },
    {
      label: t('OCPPTransactionid'),
      accessorKey: 'transactionId' as keyof CDRTransactionInterface,
    },
    {
      label: t('Account ID'),
      accessorKey: 'accountCustomId' as keyof CDRTransactionInterface,
      sortAllow: true,
      queryParamsKey: 'accountCustomId',
      queryParamsValue: 'accountCustomId',
      sidePanelComponent: (
        <FacilityDetailsSidePanel
          actionActive
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
          onRefreshList={handleRefresh}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('Charging Point'),
      accessorKey: 'chargingPointSn' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('Location ID'),
      accessorKey: 'cdrLocation' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'id',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * Location ID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Location ID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.cdrLocation?.id) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('stationId', row.cdrLocation.id);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.id || '-'}
        </span>
      ),
    },
    {
      label: t('EVSE UID'),
      accessorKey: 'cdrLocation' as keyof CDRTransactionInterface,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * EVSE UID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} EVSE UID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            if (!row.chargingPointSn) return;
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'accountCustomId',
              'deviceId',
              'connectorDeviceId',
              'stationId',
              'facilityId',
              'userCustomId',
              'userId',
              'utilityId',
              'transactionObjectId',
              'roleId',
              'sessionId',
              'cdrObjectId',
              'paymentDetailsId',
              'personalChargerId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('deviceId', row.chargingPointSn);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'Transactions',
                value: row.id,
              }),
            );
          }}>
          {row.cdrLocation?.evseUid || '-'}
        </span>
      ),
    },
    {
      label: t('connectorId'),
      accessorKey: 'cdrLocation' as keyof CDRTransactionInterface,
      /**
       * Connector ID custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Connector ID element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{row.cdrLocation?.connectorId || '-'}</span>
      ),
    },
    {
      label: t('tariffName'),
      accessorKey: 'tariffs' as keyof CDRTransactionInterface,
      /**
       * Tariff name custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Tariff name element
       */
      customData: (row: CDRTransactionInterface) => <span>{row.tariffs?.[0]?.name || '-'}</span>,
    },
    {
      label: t('Energy (kWh)'),
      accessorKey: 'totalEnergy' as keyof CDRTransactionInterface,
      /**
       * Energy custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Energy element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>{ToFixedValue(row.totalEnergy,3) || '0.000'} kWh</span>
      ),
    },
    {
      label: t('Total Cost'),
      accessorKey: 'totalCost' as keyof CDRTransactionInterface,
      /**
       * Total cost custom formatter
       * @param {CDRTransactionInterface} row - Row data
       * @returns {any} Formatted total cost element
       */
      customData: (row: CDRTransactionInterface) => (
        <span>
          {row.totalCost?.inclVat !== undefined ? ToFixedValue(row.totalCost.inclVat) : '0.00'}{' '}
          {row.currency}
        </span>
      ),
    },
    {
      label: t('Total Time'),
      accessorKey: 'totalTime' as keyof CDRTransactionInterface,
      /**
       * Render duration in formatted string.
       * @param {CDRTransactionInterface} rowData - The row data.
       * @returns {any} The total duration element.
       */
      customData: (rowData: CDRTransactionInterface) => (
        <span>{formatTotalTime(rowData.totalTime)}</span>
      ),
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column definitions for the table export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDR ID'), accessorKey: 'cdrId' },
    { label: t('Session ID'), accessorKey: 'sessionId' },
    { label: t('OCPPTransactionid'), accessorKey: 'transactionId' },
    { label: t('Account ID'), accessorKey: 'accountCustomId' },
    { label: t('Charging Point'), accessorKey: 'chargingPointSn' },
    { label: t('Location ID'), accessorKey: 'locationId' },
    { label: t('EVSE UID'), accessorKey: 'evseUid' },
    { label: t('connectorId'), accessorKey: 'connector' },
    { label: t('tariffName'), accessorKey: 'tariff' },
    { label: t('Energy (kWh)'), accessorKey: 'totalEnergy' },
    { label: t('Total Cost'), accessorKey: 'totalCost' },
    { label: t('Total Time'), accessorKey: 'totalTime' },
  ];

  /**
   * Table pagination function
   * @param {any} value - Pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    const apiCall = cpoTransactionApi.fetchCpoTransactionsExport(payload);

    apiCall
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: CDRTransactionInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'cdrId':
                return row.cdrId || t('na');
              case 'sessionId':
                return row.sessionId || t('na');
              case 'transactionId':
                return row.transactionId || t('na');
              case 'accountCustomId':
                return row.accountCustomId || t('na');
              case 'chargingPointSn':
                return row.chargingPointSn || t('na');
              case 'locationId':
                return row.cdrLocation?.id || '-';
              case 'evseUid':
                return row.cdrLocation?.evseUid || '-';
              case 'connector':
                return row.cdrLocation?.connectorId || '-';
              case 'tariff':
                return row.tariffs?.[0]?.name || '-';
              case 'totalEnergy':
                return ToFixedValue(row.totalEnergy) ?? '0.00';
              case 'totalCost':
                return row.totalCost?.inclVat !== undefined
                  ? `${ToFixedValue(row.totalCost.inclVat)} ${row.currency}`
                  : `0.00 ${row.currency ?? ''}`;
              case 'totalTime':
                return formatTotalTime(row.totalTime);
              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'payments-list.csv');
          } else {
            exportPDF(headers, rows, 'payments-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData object containing sortField and sortBy
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || payments.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPayments({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('transactions'),
        icon: <TransactionIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('userCustomId') ||
      searchParameters.get('transactionObjectId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('userCustomId');
      parameters.delete('transactionObjectId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="payment-management-wrap">
      <div className="table-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          data={payments}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          created
          lastUpdated
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPaymentMethodFilters}
          requiredRefreshToggle
          refreshRequired
          refreshFunction={handleAutoRefresh}
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('cpoTransactionExport') || permissionCheck('partnerTransactionExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
        />
      </div>
    </div>
  );
}

export default CPOTransactionManagement;
